<template>
	<article class="activity-detail-card">
		<ActivityDetailHeader
			v-if="showHeader"
			:notification-topic="activity.notificationTopic"
			:title="activity.title"
			:timestamp="activity.timestamp"
		/>

		<div class="opportunities" :aria-busy="loading">
			<ActivityDetailState
				:loading="loading"
				:error="loadError"
				:empty="opportunities.length === 0"
				:loading-message="t('activityPage.details.loading')"
				:error-message="t('activityPage.details.loadError')"
				:empty-message="t('activityPage.details.empty')"
			>
				<ActivityOpportunityCard
					v-for="opportunity in opportunities"
					:key="opportunity.id"
					:opportunity="opportunity"
				/>
			</ActivityDetailState>
		</div>

		<ActivityLinkType
			v-if="activity.linkType === LinkType.SAVED_SEARCH"
			:link-type="LinkType.SAVED_SEARCH"
			:title="activity.savedSearch"
			:summary="savedSearchSummary"
			:button-enabled="Boolean(activity.savedSearchId)"
			@open="viewSearch"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import ActivityDetailHeader from "@/components/activity/ActivityDetailHeader.vue"
import ActivityDetailState from "@/components/activity/ActivityDetailState.vue"
import ActivityLinkType from "@/components/activity/ActivityLinkType.vue"
import ActivityOpportunityCard from "@/components/activity/ActivityOpportunityCard.vue"
import { LinkType, type ActivityDetail } from "@/types/activity"

const props = withDefaults(
	defineProps<{
		activityDetail: ActivityDetail
		loading?: boolean
		loadError?: boolean
		showHeader?: boolean
	}>(),
	{
		loading: false,
		loadError: false,
		showHeader: true,
	}
)

const emit = defineEmits<{
	"view-search": [savedSearchId: string]
}>()

const { t } = useI18n()
const activity = computed(() => props.activityDetail.activity)
const opportunities = computed(() => props.activityDetail.opportunities)

const savedSearchSummary = computed(() => {
	if (!props.activityDetail.savedSearch) {
		return null
	}

	const filterCount = viewNumber(props.activityDetail.savedSearch.displayConfig, "filterCount")
	const layerCount = viewNumber(props.activityDetail.savedSearch.displayConfig, "layerCount")

	return t("activityPage.details.searchSummary", { filters: filterCount, layers: layerCount })
})

function viewNumber(config: Record<string, unknown>, key: string): number {
	const value = config[key]

	return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function viewSearch(): void {
	if (!activity.value.savedSearchId) {
		return
	}

	emit("view-search", activity.value.savedSearchId)
}
</script>

<style scoped>
.activity-detail-card {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xl);
	width: min(100%, 540px);
	font-family: var(--font-family-primary);
}

.opportunities {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
}
</style>
